import { BarChart3, ChevronRight, Flag, ListChecks, Plus, Settings, Target } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { formatDateValue, toNumber } from '@/lib/utils';

import { MetaCard } from '../components/meta-card';
import { MetasStats } from '../components/metas-stats';
import { useMetasDashboard } from '../hooks/use-metas';
import { avance, diasRestantes, TONO_A_BADGE, TIPO_LABELS } from '../lib/metas-format';
import type { Goal, GoalType } from '../metas.types';

/**
 * Panel principal de metas (`/metas`).
 *
 * Réplica del dashboard del panel anterior: indicadores, metas activas, próximos
 * vencimientos, cumplidas del mes y desempeño por tipo.
 */
export function MetasDashboardPage() {
  useDocumentTitle('Metas');

  const { data, isPending, isError, refetch, isRefetching } = useMetasDashboard();

  const stats = data?.stats;
  const activas = data?.active_goals ?? [];
  const proximos = data?.upcoming_deadlines ?? [];
  const cumplidas = data?.completed_this_month ?? [];
  const porTipo = data?.performance_by_type ?? [];

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Seguimiento</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Target className="size-6 text-brand-500" aria-hidden="true" />
            Metas
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Cumplimiento de objetivos de volumen, monto y productividad de la estación.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/metas/listado"
            className="inline-flex items-center gap-2 rounded-control border border-ink-200 bg-white px-3 py-2 text-xs font-semibold text-ink-700 transition-colors hover:border-brand-300 hover:text-brand-700"
          >
            <ListChecks className="size-3.5" aria-hidden="true" />
            Listado
          </Link>
          <Link
            to="/metas/reportes"
            className="inline-flex items-center gap-2 rounded-control border border-ink-200 bg-white px-3 py-2 text-xs font-semibold text-ink-700 transition-colors hover:border-brand-300 hover:text-brand-700"
          >
            <BarChart3 className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <Link
            to="/metas/configuracion"
            className="inline-flex items-center gap-2 rounded-control border border-ink-200 bg-white px-3 py-2 text-xs font-semibold text-ink-700 transition-colors hover:border-brand-300 hover:text-brand-700"
          >
            <Settings className="size-3.5" aria-hidden="true" />
            Configuración
          </Link>
          <Link
            to="/metas/nueva"
            className="inline-flex items-center gap-2 rounded-control bg-brand-600 px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-700"
          >
            <Plus className="size-3.5" aria-hidden="true" />
            Nueva meta
          </Link>
        </div>
      </header>

      {isError ? (
        <Alert variant="error" title="No se pudieron cargar las metas">
          <div className="flex flex-wrap items-center gap-3">
            <span>Verifica la conexión con el servidor e inténtalo nuevamente.</span>
            <Button size="sm" variant="outline" onClick={() => void refetch()} loading={isRefetching}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      <MetasStats stats={stats} loading={isPending} />

      <div className="grid items-start gap-6 xl:grid-cols-3">
        <SectionCard
          className="min-w-0 xl:col-span-2"
          title="Metas activas"
          description="Objetivos vigentes y su avance"
        >
          {isPending ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {Array.from({ length: 2 }).map((_, index) => (
                <Skeleton key={index} className="h-40 rounded-card" />
              ))}
            </div>
          ) : activas.length === 0 ? (
            <div className="rounded-control border border-dashed border-ink-200 px-4 py-10 text-center">
              <Flag className="mx-auto size-5 text-ink-300" aria-hidden="true" />
              <p className="mt-2 text-sm font-medium text-ink-700">No hay metas activas</p>
              <p className="mt-0.5 text-xs text-ink-500">
                Crea una meta para empezar a hacer seguimiento del cumplimiento.
              </p>
              <Link
                to="/metas/nueva"
                className="mt-4 inline-flex items-center gap-1.5 rounded-control bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-700"
              >
                <Plus className="size-3.5" aria-hidden="true" />
                Nueva meta
              </Link>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {activas.map((goal) => (
                <MetaCard key={goal.id} goal={goal} />
              ))}
            </div>
          )}
        </SectionCard>

        <div className="min-w-0 space-y-6">
          <SectionCard
            title="Próximos vencimientos"
            description="Metas que vencen en los próximos 7 días"
          >
            <ListaCompacta goals={proximos} loading={isPending} vacio="Sin vencimientos próximos." />
          </SectionCard>

          <SectionCard title="Cumplidas este mes" description="Metas cerradas en el mes en curso">
            <ListaCompacta goals={cumplidas} loading={isPending} vacio="Todavía no hay metas cumplidas este mes." />
          </SectionCard>
        </div>
      </div>

      <SectionCard title="Desempeño por tipo" description="Avance promedio de las metas activas por tipo">
        {isPending ? (
          <Skeleton className="h-24 rounded-control" />
        ) : porTipo.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-500">Sin metas para analizar.</p>
        ) : (
          <ul className="space-y-4">
            {porTipo.map((fila) => {
              const promedio = Math.min(Math.max(toNumber(fila.avg_progress), 0), 100);

              return (
                <li key={fila.goal_type}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium text-ink-800">
                      {TIPO_LABELS[fila.goal_type as GoalType] ?? fila.goal_type}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-ink-500">
                      <Badge variant="neutral">{fila.total} metas</Badge>
                      <span className="font-mono tabular-nums">{promedio.toFixed(1)}%</span>
                    </span>
                  </div>

                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-ink-100">
                    <div
                      style={{ width: `${promedio}%` }}
                      className="h-full rounded-full bg-gradient-to-r from-brand-500 to-accent-400"
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

interface ListaCompactaProps {
  goals: Goal[];
  loading: boolean;
  vacio: string;
}

/** Lista breve de metas (vencimientos, cumplidas). */
function ListaCompacta({ goals, loading, vacio }: ListaCompactaProps) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton key={index} className="h-14 rounded-control" />
        ))}
      </div>
    );
  }

  if (goals.length === 0) {
    return <p className="py-6 text-center text-xs text-ink-500">{vacio}</p>;
  }

  return (
    <ul className="space-y-2">
      {goals.map((goal) => {
        const porcentaje = avance(goal);
        const desempeno = goal.performance_status ?? 'on_track';

        return (
          <li key={goal.id}>
            <Link
              to={`/metas/${goal.id}`}
              className="flex items-center justify-between gap-3 rounded-control border border-ink-100 px-3 py-2 transition-colors hover:border-brand-200 hover:bg-brand-50/40"
            >
              <span className="min-w-0">
                <span className="block truncate text-xs font-medium text-ink-800">{goal.goal_name}</span>
                <span className="text-[11px] text-ink-400">
                  {formatDateValue(goal.end_date)} · {diasRestantes(goal.days_remaining)}
                </span>
              </span>

              <span className="flex shrink-0 items-center gap-2">
                <Badge variant={TONO_A_BADGE[desempeno === 'completed' ? 'emerald' : desempeno === 'warning' ? 'amber' : desempeno === 'behind' ? 'rose' : 'brand']}>
                  {porcentaje.toFixed(0)}%
                </Badge>
                <ChevronRight className="size-3.5 text-ink-300" aria-hidden="true" />
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
